import { Injectable, inject } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { FormatService } from './format.service';

Chart.register(...registerables);

export interface BreakdownSegment {
  label: string;
  value: number;
  color: string;
}

export interface GrowthSeries {
  label: string;
  data: (number | null)[];
  color: string;
  dashed?: boolean;
}

function compactINR(v: number): string {
  const n = Number(v);
  if (n >= 1_00_00_000) return '₹' + (n / 1_00_00_000).toFixed(1) + 'Cr';
  if (n >= 1_00_000) return '₹' + (n / 1_00_000).toFixed(0) + 'L';
  return '₹' + n;
}

@Injectable()
export class InvestmentChartService {
  private fmt = inject(FormatService);

  private doughnut: Chart | null = null;
  private growth: Chart | null = null;

  renderBreakdown(canvas: HTMLCanvasElement, segments: BreakdownSegment[], centreLabel = 'Total'): void {
    this.doughnut?.destroy();
    Chart.getChart(canvas)?.destroy();
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    const fmt = this.fmt;

    this.doughnut = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: segments.map(s => `${s.label}  ${total > 0 ? ((s.value / total) * 100).toFixed(1) : '0'}%`),
        datasets: [{
          data: segments.map(s => s.value),
          backgroundColor: segments.map(s => s.color),
          borderColor: '#ffffff',
          borderWidth: 4,
          hoverOffset: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '65%',
        animation: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { font: { size: 11, weight: 'bold' }, padding: 16, usePointStyle: true, pointStyle: 'circle', color: '#475569' }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12, weight: 'bold' },
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 8,
            callbacks: { label: (ctx: any) => `  ${fmt.currency(ctx.raw as number)}` }
          }
        }
      },
      plugins: [{
        id: 'doughnutCentre',
        afterDraw(chart: any) {
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return;
          const cx = (chartArea.left + chartArea.right) / 2;
          const cy = (chartArea.top + chartArea.bottom) / 2;
          c.save();
          c.textAlign = 'center';
          c.textBaseline = 'middle';
          c.font = '600 11px Segoe UI, system-ui, sans-serif';
          c.fillStyle = '#94a3b8';
          c.fillText(centreLabel, cx, cy - 11);
          c.font = 'bold 13px Segoe UI, system-ui, sans-serif';
          c.fillStyle = '#1e293b';
          c.fillText(fmt.currency(total), cx, cy + 10);
          c.restore();
        }
      }]
    } as unknown as ChartConfiguration);
  }

  // Single horizontal 100%-stacked bar for a part-to-whole split (e.g. TDS vs net amount).
  // Segments can be wildly skewed (1% vs 99%), so the legend — not bar width — carries
  // the percentage/value for every segment; a thin sliver is never the only source of truth.
  renderBreakdownBar(canvas: HTMLCanvasElement, segments: BreakdownSegment[]): void {
    this.doughnut?.destroy();
    Chart.getChart(canvas)?.destroy();
    const fmt = this.fmt;
    const total = segments.reduce((s, seg) => s + seg.value, 0);

    this.doughnut = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: [''],
        datasets: segments.map(s => ({
          label: `${s.label}  ${total > 0 ? ((s.value / total) * 100).toFixed(1) : '0'}%`,
          data: [s.value],
          backgroundColor: s.color,
          hoverBackgroundColor: s.color,
          stack: 'total',
          barPercentage: 0.55
        }))
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,.04)', lineWidth: 1 },
            border: { display: false, dash: [4, 4] },
            ticks: { font: { size: 10 }, color: '#94a3b8', callback: (v: any) => compactINR(Number(v)) }
          },
          y: {
            stacked: true,
            display: false
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { font: { size: 11, weight: 'bold' }, padding: 16, usePointStyle: true, pointStyle: 'rect', color: '#475569' }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12, weight: 'bold' },
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 8,
            callbacks: { label: (ctx: any) => `  ${ctx.dataset.label.split('  ')[0]}: ${fmt.currency(ctx.raw as number)}` }
          }
        }
      },
      plugins: [{
        id: 'stackedBarValueLabels',
        afterDatasetsDraw(chart: any) {
          const { ctx: c, scales } = chart;
          const xScale = scales['x'];
          const yScale = scales['y'];
          const centerY = yScale.getPixelForValue(0);
          let cumulative = 0;
          chart.data.datasets.forEach((ds: any) => {
            const value = ds.data[0] as number;
            const xStart = xScale.getPixelForValue(cumulative);
            const xEnd = xScale.getPixelForValue(cumulative + value);
            cumulative += value;
            const segWidth = xEnd - xStart;
            const text = fmt.currency(value);
            c.save();
            c.font = 'bold 11px Segoe UI, system-ui, sans-serif';
            const textWidth = c.measureText(text).width;
            if (segWidth - 16 >= textWidth) {
              c.fillStyle = '#ffffff';
              c.textAlign = 'center';
              c.textBaseline = 'middle';
              c.fillText(text, (xStart + xEnd) / 2, centerY);
            }
            c.restore();
          });
        }
      }]
    } as unknown as ChartConfiguration);
  }

  renderGrowth(canvas: HTMLCanvasElement, labels: (string | number)[], series: GrowthSeries[]): void {
    this.growth?.destroy();
    Chart.getChart(canvas)?.destroy();
    const fmt = this.fmt;

    this.growth = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: series.map(s => ({
          label: s.label,
          data: s.data,
          borderColor: s.color,
          borderWidth: s.dashed ? 2 : 2.5,
          borderDash: s.dashed ? [6, 4] : undefined,
          backgroundColor: s.dashed ? 'transparent' : s.color + '12',
          fill: !s.dashed,
          tension: 0.38,
          pointRadius: 2.5,
          pointHoverRadius: 6,
          pointBackgroundColor: s.color,
          spanGaps: true
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 10 }, color: '#94a3b8' }
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(0,0,0,.04)', lineWidth: 1 },
            border: { display: false, dash: [4, 4] },
            ticks: { font: { size: 10 }, color: '#94a3b8', callback: (v) => compactINR(Number(v)) }
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { font: { size: 11, weight: 'bold' }, padding: 14, usePointStyle: true, pointStyle: 'line', color: '#475569' }
          },
          tooltip: {
            backgroundColor: '#1e293b',
            titleFont: { size: 12, weight: 'bold' },
            bodyFont: { size: 11 },
            padding: 12,
            cornerRadius: 8,
            callbacks: { label: (ctx: any) => `  ${ctx.dataset.label}: ${fmt.currency(ctx.raw as number)}` }
          }
        }
      }
    });
  }

  destroyAll(): void {
    this.doughnut?.destroy(); this.doughnut = null;
    this.growth?.destroy();   this.growth   = null;
  }
}
